import {
  AfterViewInit,
  Component,
  OnDestroy,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FullCalendarComponent,
  FullCalendarModule,
} from '@fullcalendar/angular';
import esLocale from '@fullcalendar/core/locales/es';
import {
  CalendarOptions,
  DateSelectArg,
  EventClickArg,
  DatesSetArg,
  EventInput,
  CalendarApi,
  EventApi,
  EventChangeArg,
  DateSpanApi,
} from '@fullcalendar/core';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import {
  AgendaFilterService,
  DateSelectionService,
  OnboardingService,
  PageTitleService,
  ToastService,
} from '../../shared/services';
import { AgendaDemoStore } from './agenda-demo.store';
import { Modal } from '../../shared/components/modal/modal';
import { EventAdd } from '../../shared/components/event-add/event-add';
import { EventView } from '../../shared/components/event-view/event-view';
import { AgendaService, BusinessService } from '../../services/business';
import {
  ScheduleService,
  BusinessScheduleRange,
} from '../../services/business/schedule.service';
import {
  Agenda,
  AgendaEvent,
  CreateAgendaEventDto,
  GetAgendaEventsParams,
  UpdateAgendaEventDto,
  Professional,
} from '../../shared/interfaces';
import { BaseAuthComponent } from '../../shared/common/BaseAuthComponent.class';
import hexToRgba, {
  mergeDateAndTime,
  parseDbUtc,
} from '../../shared/common/utils';
import { STATUS_DEF_MAP } from '../../shared/api/eventStatus.token';
import {
  concat,
  debounceTime,
  distinctUntilChanged,
  fromEvent,
  map,
  startWith,
  toArray,
} from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmModal } from '../../shared/components/confirm-modal/confirm-modal';
import { MembershipService } from '../../services/membership/membership.service';
import { RouterLink } from '@angular/router';
import { ScheduleBlockAdd } from '../../shared/components/schedule-block-add/schedule-block-add';

@Component({
  selector: 'app-agendas',
  imports: [
    CommonModule,
    FullCalendarModule,
    Modal,
    ConfirmModal,
    RouterLink,
  ],
  templateUrl: './agendas.html',
  styleUrl: './agendas.css',
})
export class Agendas
  extends BaseAuthComponent
  implements AfterViewInit, OnDestroy
{
  private destroyRef = inject(DestroyRef);
  @ViewChild('calendar')
  set calendarRef(c: FullCalendarComponent | undefined) {
    if (!c) return;
    this.calendar = c;
    this.fc = c.getApi();
    this.applyPendingEvents();
  }
  @ViewChild('calWrap', { read: ElementRef }) calWrap!: ElementRef<HTMLElement>;
  calendar?: FullCalendarComponent;
  private fc!: CalendarApi;
  calendarVisible = signal(true);
  calendarLocales = [esLocale];
  private store = inject(AgendaDemoStore);
  private agendaApi = inject(AgendaService);
  private businessService = inject(BusinessService);
  private scheduleService = inject(ScheduleService);
  private membershipService = inject(MembershipService);
  private onboardingService = inject(OnboardingService);
  toastService = inject(ToastService);
  EventAddForm = EventAdd;
  AgendaEventView = EventView;
  ScheduleBlockAddForm = ScheduleBlockAdd;
  open = false;
  openForm = false;
  openBlockForm = false;
  titleSelected = signal<string>('');
  private baseTitle = 'Crear evento'; // Store base title (Crear evento / Editar evento)
  modalBodyInputs: Record<string, unknown> = {};
  modalBodyInputsEventView: Record<string, unknown> = {};
  modalBodyInputsBlock: Record<string, unknown> = {};
  private dateBus = inject(DateSelectionService);
  private ro?: ResizeObserver;
  confirmOpen = false;
  toDeleteId: string | null = null;
  outsideHoursConfirmOpen = false;
  pendingSelection: { start: Date; end: Date } | null = null;
  deleteBlockConfirmOpen = false;
  blockToDelete: { id: string; title?: string } | null = null;
  paymentDrawerOpen = signal(false);
  drawerEventId = signal<string | null>(null);
  eventModalSize = signal<'sm' | 'md' | 'md-1' | 'lg' | 'xl' | '2xl'>('sm');

  savingEvent = signal(false);
  savingBlock = signal(false);
  private statusDefs = inject(STATUS_DEF_MAP);
  agendas = signal<Agenda[]>([]); // Kept for backward compatibility
  professionals = signal<Professional[]>([]);
  selectedEventId = signal<string | null>(null);
  businessScheduleRanges = signal<BusinessScheduleRange[]>([]);
  businessScheduleLoaded = signal(false);
  hasBusinessSchedule = computed(
    () => this.businessScheduleRanges().length > 0
  );
  checklist = this.onboardingService.status;
  showChecklist = this.onboardingService.shouldShowChecklist;

  // Toolbar state, kept in sync with FullCalendar through datesSet
  viewTitle = signal('');
  viewType = signal<string>('timeGridWeek');
  rangeHasToday = signal(true);
  isNarrow = signal(typeof window !== 'undefined' && window.innerWidth < 1024);
  viewOptions = computed(() => {
    const all = [
      { type: 'timeGridDay', label: 'Día' },
      { type: 'timeGridWeek', label: 'Semana' },
      { type: 'dayGridMonth', label: 'Mes' },
      { type: 'listWeek', label: 'Lista' },
    ];
    // Week and month grids are unreadable on small screens
    return this.isNarrow()
      ? all.filter((v) => v.type === 'timeGridDay' || v.type === 'listWeek')
      : all;
  });
  canSchedule = computed(
    () =>
      !this.onboardingService.shouldShowChecklist() &&
      !this.membershipService.isMembershipExpired()
  );
  statusLegend = computed(() =>
    (['scheduled', 'confirmed', 'completed', 'cancelled'] as const)
      .map((code) => this.statusDefs[code])
      .filter((def) => !!def)
      .map((def) => ({ label: this.statusLabel(def.name), color: def.color ?? '#9CA3AF' }))
  );
  checklistLoading = this.onboardingService.isLoading;
  onboardingProgress = this.onboardingService.progressPercent;
  onboardingCompletedSteps = this.onboardingService.completedSteps;
  onboardingTotalSteps = this.onboardingService.totalSteps;
  onboardingPendingSteps = computed(
    () => this.onboardingTotalSteps - this.onboardingCompletedSteps()
  );
  /** Setup steps in order; the first pending one is the current step */
  setupSteps = computed(() => {
    const c = this.checklist();
    const steps = [
      {
        key: 'professional',
        done: c.hasProfessionals,
        title: 'Agrega a quien atiende',
        description: 'Al menos un profesional que reciba citas en el calendario.',
        doneDescription: 'Tu perfil profesional ya está en la agenda.',
        link: '/settings/professionals',
        action: 'Agregar profesional',
      },
      {
        key: 'service',
        done: c.hasServices,
        title: 'Crea tu primer servicio',
        description: 'Nombre, duración y precio. Es lo que tus clientes eligen al reservar.',
        doneDescription: 'Ya tienes servicios para reservar.',
        link: '/settings/services',
        action: 'Crear servicio',
      },
      {
        key: 'schedule',
        done: c.hasSchedule,
        title: 'Define tu horario de atención',
        description: 'Los días y horas en que aceptas citas.',
        doneDescription: 'Tu horario de atención está definido.',
        link: '/settings/business-hours',
        action: 'Definir horario',
      },
    ];
    const currentKey = steps.find((s) => !s.done)?.key;
    return steps.map((s) => ({ ...s, current: s.key === currentKey }));
  });
  /** Mini week shown next to the setup steps (Mon-Sat open, Sun closed) */
  readonly previewDays = [
    { label: 'L', open: true },
    { label: 'M', open: true },
    { label: 'M', open: true },
    { label: 'J', open: true },
    { label: 'V', open: true },
    { label: 'S', open: true },
    { label: 'D', open: false },
  ];
  /** Sample appointments for the preview: day column, top and height in % */
  readonly previewAppointments = [
    { day: 0, top: 18, height: 22 },
    { day: 1, top: 44, height: 30 },
    { day: 2, top: 12, height: 18 },
    { day: 3, top: 36, height: 22 },
    { day: 3, top: 66, height: 16 },
    { day: 4, top: 20, height: 34 },
    { day: 5, top: 30, height: 20 },
  ];
  professionalById = computed(() => {
    const map = new Map<string, string>();
    for (const p of this.professionals()) {
      map.set(p.userId, p?.color || '#3B82F6');
    }
    return map;
  });
  // Kept for backward compatibility
  agendaById = computed(() => this.professionalById());

  private lastFetchedEvents: AgendaEvent[] = [];
  private pendingEvents: EventInput[] | null = null;
  constructor(
    private titleService: PageTitleService,
    private filterSvc: AgendaFilterService
  ) {
    super();
    effect(() => {
      const idsVisibles = this.filterSvc.filteredIds();
      this.applyFilterToCalendar();
      this.calendar?.getApi().refetchEvents();
      const d = this.dateBus.selectedDate();
      if (!d) return;
      this.fc.gotoDate(d);
    });
    effect(() => {
      const canSchedule =
        !this.onboardingService.shouldShowChecklist() &&
        !this.membershipService.isMembershipExpired();
      this.calendarOptions.update((opts) => ({
        ...opts,
        selectable: canSchedule,
        editable: canSchedule,
      }));
    });
    // Refresh calendar when an event is created from outside (e.g. header menu)
    effect(() => {
      const tick = this.agendaApi.eventCreatedTick();
      if (tick > 0) {
        this.loadVisibleRange();
      }
    });
  }

  private loadVisibleRange() {
    if (!this.fc) return;
    const v = this.fc.view;
    this.loadEvents({
      startDate: v.currentStart.toISOString(),
      endDate: v.currentEnd.toISOString(),
      businessId: this.businessId,
    });
  }

  loadEvents(filters: GetAgendaEventsParams = {}) {
    // Additional defensive check before making API call
    if (this.membershipService.isMembershipExpired()) {
      return;
    }

    this.preloader.show();

    // Load events and schedule blocks in parallel
    const events$ = this.agendaApi.getAgendaEvents(filters);
    const startDateStr =
      filters.startDate instanceof Date
        ? filters.startDate.toISOString()
        : filters.startDate;
    const endDateStr =
      filters.endDate instanceof Date
        ? filters.endDate.toISOString()
        : filters.endDate;
    const blocks$ = this.scheduleService.getScheduleBlocks(
      this.businessId,
      undefined, // All agendas
      startDateStr,
      endDateStr
    );

    // Combine both observables
    events$.subscribe({
      next: (list) => {
        this.lastFetchedEvents = list;
        // Load blocks after events
        blocks$.subscribe({
          next: (blocksResponse) => {
            this.preloader.hide();
            // Convert blocks to events and add to list
            const blockEvents = this.convertBlocksToEvents(
              blocksResponse.blocks
            );
            this.lastFetchedEvents = [...list, ...blockEvents];
            this.applyFilterToCalendar();
          },
          error: (err) => {
            this.preloader.hide();
            console.error('Error cargando bloqueos:', err);
            // Still apply events even if blocks fail
            this.applyFilterToCalendar();
          },
        });
      },
      error: (err) => {
        this.preloader.hide();
        console.error('Error cargando eventos:', err);
      },
    });
  }

  private convertBlocksToEvents(blocks: any[]): AgendaEvent[] {
    return blocks.flatMap((block) => {
      const events: AgendaEvent[] = [];
      // Blocks are whole calendar days: read only the date part, as a local date
      const localDay = (value: string) => {
        const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
        return new Date(y, m - 1, d);
      };
      const startDate = localDay(block.startDate);
      const endDate = localDay(block.endDate);

      // Generate events for each day in the date range
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const [startHour, startMinute] = block.startTime.split(':').map(Number);
        const [endHour, endMinute] = block.endTime.split(':').map(Number);

        const blockStart = new Date(currentDate);
        blockStart.setHours(startHour, startMinute, 0, 0);

        const blockEnd = new Date(currentDate);
        blockEnd.setHours(endHour, endMinute, 0, 0);

        events.push({
          eventId: `${block.scheduleBlockId}-${
            currentDate.toISOString().split('T')[0]
          }`,
          professionalId: block.professionalId || block.agendaId || '',
          agendaId: block.agendaId || block.professionalId || '', // Backward compatibility
          clientId: '',
          serviceId: '',
          statusId: '',
          startDate: blockStart.toISOString(),
          endDate: blockEnd.toISOString(),
          notes: block.title || block.description || 'Bloqueo',
          paymentCompleted: false,
          statusCode: 'blocked',
          editable: false, // Prevent editing blocks at event level
          startEditable: false,
          durationEditable: false,
          resourceEditable: false,
          extendedProps: {
            type: 'block',
            scheduleBlockId: block.scheduleBlockId,
            title: block.title || 'Bloqueo',
          },
        } as any);

        currentDate.setDate(currentDate.getDate() + 1);
      }

      return events;
    });
  }

  ngAfterViewInit() {
    // Only initialize calendar if membership is not expired
    if (this.membershipService.isMembershipExpired()) {
      return;
    }

    this.ro = new ResizeObserver(() => {
      const api = this.calendar?.getApi();
      if (!api) return;
      //this.applyResponsiveView(initialView);
      queueMicrotask(() => api.updateSize());
    });
    this.ro.observe(this.calWrap.nativeElement);
    this.loadVisibleRange();
    const initialView = this.viewForWidth(window.innerWidth);
    this.applyResponsiveView(initialView);
    fromEvent(window, 'resize')
      .pipe(
        debounceTime(150),
        map(() => this.viewForWidth(window.innerWidth)),
        startWith(initialView),
        distinctUntilChanged()
      )
      .subscribe((view) => this.applyResponsiveView(view));

  }

  ngOnDestroy(): void {
    this.ro?.disconnect();
    this.ro = undefined;
  }

  ngOnInit(): void {
    this.titleService.setPage({
      title: 'Calendario',
      subtitle: 'Agenda, mueve y confirma las citas de tu equipo',
    });
    // Always verify against the backend on entry, never trust a cached "complete"
    this.onboardingService.checkPrerequisites(true);

    // Only load agendas if membership is not expired
    if (!this.membershipService.isMembershipExpired()) {
      this.loadAgendas();
    }
  }


  closePaymentDrawer() {
    this.paymentDrawerOpen.set(false);
    this.drawerEventId.set(null);
  }

  onPaymentRegistered() {
    // Reload events to reflect payment status changes
    this.loadVisibleRange();
  }

  loadAgendas() {
    // Additional defensive check before making API call
    if (this.membershipService.isMembershipExpired()) {
      return;
    }

    // Load professionals instead of agendas
    this.businessService
      .searchEntityByBusinessId(this.businessId, 'professionals', '', 1, 100)
      .subscribe({
        next: (list) => {
          const professionals = list as Professional[];
          this.professionals.set(professionals);
          // Map professionals to agendas format for backward compatibility
          this.agendas.set(
            professionals.map((p) => ({
              agendaId: p.userId,
              businessId: this.businessId,
              professionalId: p.userId,
              name: `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.email || 'Profesional',
              description: p.email || null,
              color: p.color || '#3B82F6',
              colorSoft: hexToRgba(p.color ?? '#3B82F6', 0.08),
              createdAt: p.createdAt,
              updatedAt: null,
            } as Agenda))
          );
        },
        error: (e) => {
          console.error('Error loading professionals:', e);
          this.toastService.show('Error al cargar profesionales', 'error');
        },
      });

    // Load business schedule and update calendar businessHours
    this.scheduleService.getBusinessSchedule(this.businessId).subscribe({
      next: (schedule) => {
        const activeRanges =
          schedule.timeRanges?.filter((range) => range.isActive) ?? [];
        this.businessScheduleRanges.set(activeRanges);
        this.businessScheduleLoaded.set(true);

        const businessHours = activeRanges.map((range) => ({
          daysOfWeek: [range.dayOfWeek],
          startTime: range.startTime,
          endTime: range.endTime,
        }));

        // Calculate time range from schedule (use defaults only for slot display)
        const timeRange = this.calculateTimeRangeFromRanges(activeRanges);

        // Update calendar options with business hours and time range
        this.calendarOptions.update((opts) => ({
          ...opts,
          businessHours,
          slotMinTime: timeRange.minTime,
          slotMaxTime: timeRange.maxTime,
        }));

        // Update calendar if it's already initialized
        if (this.fc) {
          this.fc.setOption('businessHours', businessHours);
          this.fc.setOption('slotMinTime', timeRange.minTime);
          this.fc.setOption('slotMaxTime', timeRange.maxTime);
        }
      },
      error: (e) => {
        // If error (404 or no schedules), keep calendar without business hours
        this.businessScheduleRanges.set([]);
        this.businessScheduleLoaded.set(true);

        const defaultTimeRange = { minTime: '06:00:00', maxTime: '21:00:00' };

        this.calendarOptions.update((opts) => ({
          ...opts,
          businessHours: [],
          slotMinTime: defaultTimeRange.minTime,
          slotMaxTime: defaultTimeRange.maxTime,
        }));

        if (this.fc) {
          this.fc.setOption('businessHours', []);
          this.fc.setOption('slotMinTime', defaultTimeRange.minTime);
          this.fc.setOption('slotMaxTime', defaultTimeRange.maxTime);
        }

        console.error('Error cargando horario del negocio:', e);
      },
    });
  }

  private calculateTimeRangeFromRanges(ranges: BusinessScheduleRange[]): {
    minTime: string;
    maxTime: string;
  } {
    if (!ranges || ranges.length === 0) {
      return { minTime: '06:00:00', maxTime: '21:00:00' };
    }

    let minHour = 24;
    let minMinute = 59;
    let maxHour = 0;
    let maxMinute = 0;

    for (const range of ranges) {
      if (!range.isActive) continue;

      const [startHour, startMinute] = range.startTime.split(':').map(Number);
      const [endHour, endMinute] = range.endTime.split(':').map(Number);

      // Find earliest start time
      if (
        startHour < minHour ||
        (startHour === minHour && startMinute < minMinute)
      ) {
        minHour = startHour;
        minMinute = startMinute;
      }

      // Find latest end time
      if (endHour > maxHour || (endHour === maxHour && endMinute > maxMinute)) {
        maxHour = endHour;
        maxMinute = endMinute;
      }
    }

    // If no valid ranges found, use defaults
    if (minHour === 24 || maxHour === 0) {
      return { minTime: '06:00:00', maxTime: '21:00:00' };
    }

    // Add some padding: 1 hour before earliest start, 1 hour after latest end
    const minTimeHour = Math.max(0, minHour - 1);
    const maxTimeHour = Math.min(23, maxHour + 1);

    const formatTime = (hour: number, minute: number) => {
      return `${String(hour).padStart(2, '0')}:${String(minute).padStart(
        2,
        '0'
      )}:00`;
    };

    return {
      minTime: formatTime(minTimeHour, minMinute),
      maxTime: formatTime(maxTimeHour, maxMinute),
    };
  }

  refreshSetupStatus() {
    this.onboardingService.checkPrerequisites(true);
  }

  private viewForWidth(width: number): CalendarOptions['initialView'] {
    if (width < 640) return 'timeGridDay';
    if (width < 1024) return 'timeGridDay';
    return 'timeGridWeek';
  }

  private applyResponsiveView(view: CalendarOptions['initialView']) {
    this.isNarrow.set(window.innerWidth < 1024);

    if (this.fc) {
      this.fc.changeView(view as any);
      this.fc.updateSize();
    }

    this.calendarOptions.update((opts) => ({
      ...opts,
      initialView: view,
    }));
  }

  // ---- Toolbar actions ----

  goPrev() {
    this.fc?.prev();
  }

  goNext() {
    this.fc?.next();
  }

  goToday() {
    this.fc?.today();
  }

  setView(type: string) {
    this.fc?.changeView(type);
  }

  /** Opens the appointment form at the next free quarter hour (or 9:00 on the first visible day) */
  onNewAppointment() {
    if (!this.canSchedule()) {
      this.toastService.show(
        'Completa la configuración inicial para poder agendar citas.',
        'info'
      );
      return;
    }

    let start: Date;
    if (this.rangeHasToday() || !this.fc) {
      start = new Date();
      start.setMinutes(Math.ceil(start.getMinutes() / 15) * 15, 0, 0);
    } else {
      start = new Date(this.fc.view.activeStart);
      start.setHours(9, 0, 0, 0);
    }

    const dateTitle = this.formatEsNoDe(start);
    this.baseTitle = dateTitle;
    this.titleSelected.set(dateTitle);
    this.modalBodyInputs = { selectedDate: start };
    this.openForm = true;
  }

  /** "Agendado" → "Agendada": the calendar talks about citas */
  statusLabel(name?: string | null): string {
    if (!name) return '';
    return name.endsWith('do') ? name.slice(0, -1) + 'a' : name;
  }

  private onSelect = (arg: DateSelectArg) => {
    if (this.onboardingService.shouldShowChecklist()) {
      this.toastService.show(
        'Completa la configuración inicial para poder agendar eventos.',
        'info'
      );
      return;
    }
    console.log(arg);
    this.openForm = true;
    const start = arg.start;
    const end = arg.allDay
      ? new Date(arg.end.getTime() - 1 * 24 * 60 * 60 * 1000)
      : arg.end;
    const sameDay = start.toDateString() === end.toDateString();
    const dateTitle = sameDay
      ? this.formatEsNoDe(start)
      : `${this.formatEsNoDe(start)} — ${this.formatEsNoDe(end)}`;
    this.baseTitle = dateTitle;
    this.titleSelected.set(dateTitle);

    this.modalBodyInputs = {
      selectedDate: start,
    };
    this.calendar?.getApi().unselect();
  };

  private formatEsNoDe(date: Date): string {
    const parts = new Intl.DateTimeFormat('es-CO', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).formatToParts(date);

    const w = parts.find((p) => p.type === 'weekday')?.value ?? '';
    const d = parts.find((p) => p.type === 'day')?.value ?? '';
    const m = parts.find((p) => p.type === 'month')?.value ?? '';

    // forzamos minúsculas y quitamos “de”
    return `${w}, ${d} ${m}`.toLocaleLowerCase('es-CO');
  }

  private onEventClick = (click: EventClickArg) => {
    // Handle schedule blocks - show delete confirmation
    const extendedProps = click.event.extendedProps as any;
    const eventType = extendedProps?.['type'];

    console.log('Event clicked:', {
      id: click.event.id,
      title: click.event.title,
      extendedProps: extendedProps,
      eventType: eventType,
    });

    if (eventType === 'block') {
      // Get the actual scheduleBlockId from extendedProps
      const scheduleBlockId = extendedProps?.['scheduleBlockId'];
      console.log('Block detected, scheduleBlockId:', scheduleBlockId);

      if (scheduleBlockId) {
        this.blockToDelete = {
          id: scheduleBlockId,
          title: click.event.title || 'Bloqueo de horario',
        };
        this.deleteBlockConfirmOpen = true;
        console.log('Opening delete modal, blockToDelete:', this.blockToDelete);
      } else {
        console.warn('Block detected but scheduleBlockId is missing');
      }
      return;
    }

    this.open = true;
    this.selectedEventId.set(click.event.id);
    this.modalBodyInputsEventView = {
      eventId: click.event.id,
    };
  };

  /** "Almuerzo, Laura" for a professional's block; just the reason for business-wide ones */
  blockLabel(event: EventApi): string {
    const pid = (event.extendedProps as any)?.professionalId;
    const reason = event.title && event.title !== 'Bloqueo' ? event.title : 'Bloqueado';
    if (!pid) return reason;
    const p = this.professionals().find((x) => x.userId === pid);
    const name = p ? `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() : '';
    return name ? `${reason}, ${name}` : reason;
  }

  isBlockEvent(event: EventApi): boolean {
    const eventType = (event.extendedProps as any)?.['type'];
    return eventType === 'block';
  }

  isEventPaid(event: EventApi): boolean {
    const props = event.extendedProps as any;
    const isPaid = !!props?.paymentCompleted;
    // Debug: uncomment to check if paymentCompleted is being passed
    // if (props) console.log('Event paymentCompleted:', props.paymentCompleted, 'isPaid:', isPaid);
    return isPaid;
  }

  private onEventDidMount = (info: any) => {
    const eventType = (info.event.extendedProps as any)?.['type'];
    const isBlock = eventType === 'block';

    if (isBlock) {
      // Hatched surface (see agendas.css); clicking it offers to unblock
      const el = info.el as HTMLElement;
      el.classList.add('ag-block-host');
      el.title = `${info.event.title || 'Horario bloqueado'}. Haz clic para desbloquear.`;
    } else {
      const c =
        info.event.extendedProps?.stripeColor ||
        info.event.backgroundColor ||
        '#3B82F6';
      const el = info.el as HTMLElement;
      el.style.setProperty('--stripe-color', c);
      el.style.setProperty('--event-tint', hexToRgba(c, 0.14));
      el.style.setProperty('--status-color', info.event.extendedProps?.statusColor || '#9CA3AF');
      el.classList.add('ag-event-host');
      if (info.event.extendedProps?.statusCode === 'cancelled') {
        el.classList.add('ag-event-host--cancelled');
      }
    }
  };

  maxConcurrency(events: { start: Date; end: Date }[]) {
    const pts: Array<{ t: number; d: 1 | -1 }> = [];
    for (const e of events) {
      pts.push({ t: +e.start, d: +1 });
      pts.push({ t: +e.end, d: -1 });
    }
    pts.sort((a, b) => (a.t === b.t ? b.d - a.d : a.t - b.t));
    let cur = 0,
      ans = 0;
    for (const p of pts) {
      cur += p.d;
      ans = Math.max(ans, cur);
    }
    return ans;
  }

  calendarOptions = signal<CalendarOptions>({
    plugins: [interactionPlugin, dayGridPlugin, timeGridPlugin, listPlugin],
    headerToolbar: false,
    initialView: 'timeGridWeek',
    locales: this.calendarLocales,
    locale: 'es',
    allDaySlot: false,
    slotLabelFormat: { hour: 'numeric', minute: '2-digit', hour12: true },
    slotLabelInterval: '01:00', // 15-minute slots, but only label full hours
    dayHeaderFormat: { weekday: 'short', day: 'numeric' },
    eventTimeFormat: { hour: 'numeric', minute: '2-digit', hour12: true },
    selectable: true,
    selectMirror: true,
    editable: true,
    dayMaxEvents: true,
    slotEventOverlap: false, // Side by side, never stacked on top of each other
    eventMinHeight: 20,
    displayEventEnd: true,
    views: {
      // In the grid the card height already shows the duration; the start time is enough
      timeGrid: { displayEventEnd: false },
    },
    height: 'auto',
    contentHeight: 'auto',
    handleWindowResize: true,
    nowIndicator: true,
    slotMinTime: '06:00:00', // Will be updated dynamically
    slotMaxTime: '21:00:00', // Will be updated dynamically
    scrollTime: '08:00:00',
    scrollTimeReset: false,
    slotDuration: '00:15:00', // Show 15-minute time slots
    snapDuration: '00:15:00', // Snap events to 15-minute intervals when dragging
    businessHours: [
      {
        daysOfWeek: [1, 2, 3, 4, 5, 6],
        startTime: '08:00',
        endTime: '12:00',
      },
      {
        daysOfWeek: [1, 2, 3, 4, 5, 6],
        startTime: '14:00',
        endTime: '18:00',
      },
    ],
    // selectConstraint: 'businessHours',
    selectAllow: (selectInfo) => this.onSelectAllow(selectInfo),
    // selectOverlap: (event) => event.extendedProps.type !== 'block',
    select: this.onSelect,
    eventClick: this.onEventClick,
    eventDidMount: this.onEventDidMount,
    eventChange: (chg) => this.onEventChange(chg),
    timeZone: 'local',
    datesSet: (arg: DatesSetArg) => {
      this.viewTitle.set(arg.view.title);
      this.viewType.set(arg.view.type);
      const now = Date.now();
      this.rangeHasToday.set(+arg.view.currentStart <= now && now < +arg.view.currentEnd);
      this.loadEvents({
        startDate: arg.startStr,
        endDate: arg.endStr,
        businessId: this.businessId,
      });
    },
  });

  onEventChange = (chg: EventChangeArg) => {
    const ev = chg.event;
    const xp = ev.extendedProps as any;

    // Prevent changes to schedule blocks
    if (xp?.type === 'block') {
      this.toastService.show(
        'Los bloqueos de horario no se pueden modificar.',
        'warning'
      );
      chg.revert();
      return;
    }

    const body: UpdateAgendaEventDto = {
      clientId: xp.clientId,
      serviceId: xp.serviceId,
      statusId: xp.statusId,
      startDate: ev.start!,
      endDate: ev.end!,
      notes: xp.notes ?? '',
      paymentCompleted: !!xp.paymentCompleted,
    };

    this.agendaApi.updateAgendaEvent(ev.id, body).subscribe({
      next: () => {
        this.toastService.show('Evento actualizado correctamente', 'success');
        this.loadVisibleRange();
      },
      error: (err: HttpErrorResponse) => {
        console.error(err);
        const message = this.extractAgendaErrorMessage(err);
        this.toastService.show(message, 'warning', 6000);
        chg.revert();
      },
    });
  };

  onSelectAllow(selectInfo: DateSpanApi): boolean {
    const { start, end } = selectInfo;

    const isInside = this.isWithinBusinessHours(start, end);

    if (!isInside) {
      // Save pending selection and open confirmation modal
      this.pendingSelection = { start, end };
      this.outsideHoursConfirmOpen = true;
      return false; // Prevent immediate selection
    }

    const blocks = this.lastFetchedEvents.filter(
      (e) => (e as any).extendedProps?.['type'] === 'block'
    );
    // A single-slot selection is a plain click: on top of a block it belongs to
    // the block (eventClick offers to unblock it), not to a new appointment
    const isClick = end.getTime() - start.getTime() <= 15 * 60 * 1000;

    for (const block of blocks) {
      const blockStart = new Date(block.startDate);
      const blockEnd = new Date(block.endDate);
      if (!(start < blockEnd && end > blockStart)) continue;

      if (isClick) return false;

      // Business-wide blocks close the slot for everyone. A professional's own block only
      // closes it for them, and the professional is chosen later in the form.
      if (!block.professionalId) {
        this.toastService.show('Este horario está bloqueado y no se puede seleccionar.', 'warning');
        return false;
      }
    }

    return true;
  }

  onConfirmOutsideHours() {
    if (!this.pendingSelection) {
      this.outsideHoursConfirmOpen = false;
      return;
    }

    // Open the event creation modal with the pending selection
    const { start, end } = this.pendingSelection;

    // Format dates and times for the modal
    // EventAdd expects Date objects for startDate/endDate and time strings for startTime/endTime
    const startDate = new Date(start);
    const endDate = new Date(end);
    const startTime = `${String(start.getHours()).padStart(2, '0')}:${String(
      start.getMinutes()
    ).padStart(2, '0')}`;
    const endTime = `${String(end.getHours()).padStart(2, '0')}:${String(
      end.getMinutes()
    ).padStart(2, '0')}`;

    this.modalBodyInputs = {
      startDate,
      endDate,
      startTime,
      endTime,
    };

    this.baseTitle = 'Crear evento';
    this.titleSelected.set('Crear evento');
    this.openForm = true;
    this.outsideHoursConfirmOpen = false;
    this.pendingSelection = null;
  }

  onCancelOutsideHours() {
    this.outsideHoursConfirmOpen = false;
    this.pendingSelection = null;
  }

  onConfirmDeleteBlock() {
    if (!this.blockToDelete) {
      this.deleteBlockConfirmOpen = false;
      return;
    }

    this.scheduleService
      .deleteScheduleBlock(this.blockToDelete.id, this.businessId)
      .subscribe({
        next: () => {
          // Remove all events related to this block (blocks can span multiple days)
          const allEvents = this.fc?.getEvents();
          if (allEvents) {
            allEvents.forEach((event) => {
              const eventProps = event.extendedProps as any;
              if (
                eventProps?.type === 'block' &&
                eventProps?.scheduleBlockId === this.blockToDelete!.id
              ) {
                event.remove();
              }
            });
          }

          this.toastService.show('Horario desbloqueado', 'success');
          this.deleteBlockConfirmOpen = false;
          this.blockToDelete = null;
          // Reload events to refresh the view
          this.loadVisibleRange();
        },
        error: (err) => {
          console.error('Error al eliminar bloqueo:', err);
          this.toastService.show(
            'No se pudo desbloquear el horario. Intenta de nuevo.',
            'error'
          );
        },
      });
  }

  onCancelDeleteBlock() {
    this.deleteBlockConfirmOpen = false;
    this.blockToDelete = null;
  }

  private isWithinBusinessHours(start: Date, end: Date): boolean {
    // Get the day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    const dayOfWeek = start.getDay();

    // Get active schedule ranges for this business
    const scheduleRanges = this.businessScheduleRanges();

    // Filter ranges for the specific day of week
    const dayRanges = scheduleRanges.filter(
      (range) => range.dayOfWeek === dayOfWeek && range.isActive
    );

    // If no schedules exist for this day, don't allow selection
    if (dayRanges.length === 0) {
      return false;
    }

    // Convert selection times to minutes
    const startMinutes = start.getHours() * 60 + start.getMinutes();
    const endMinutes = end.getHours() * 60 + end.getMinutes();

    // Check if selection falls within any of the day's time ranges
    return dayRanges.some((range) => {
      // Parse time strings (format: "HH:mm")
      const [startHour, startMin] = range.startTime.split(':').map(Number);
      const [endHour, endMin] = range.endTime.split(':').map(Number);

      const rangeStartMinutes = startHour * 60 + startMin;
      const rangeEndMinutes = endHour * 60 + endMin;

      // Selection must start at or after range start and end at or before range end
      return startMinutes >= rangeStartMinutes && endMinutes <= rangeEndMinutes;
    });
  }

  onModalConfirm(payload: any): void {
    if (!payload) {
      this.open = false;
      return;
    }
    this.savingEvent.set(true);

    // Multi-service: payload.multiService === true
    if (payload.multiService && Array.isArray(payload.events)) {
      this.createMultiServiceEvents(payload.events);
      return;
    }

    const start = mergeDateAndTime(payload.startDate, payload.startTime);
    const end = mergeDateAndTime(payload.endDate, payload.endTime);
    if (end <= start) {
      end.setDate(end.getDate() + 1);
    }

    if (!payload.eventId) {
      const body: CreateAgendaEventDto = {
        ...payload,
        startDate: start,
        endDate: end,
      };

      this.agendaApi.createAgendaEvent(body).subscribe({
        next: (res) => {
          if (res.eventId) {
            this.openForm = false;
            this.savingEvent.set(false);
            this.loadVisibleRange();
          }
        },
        error: (err: HttpErrorResponse) => {
          const errorMessage = this.extractAgendaErrorMessage(err);
          this.toastService.show(errorMessage, 'warning', 6000);
          this.savingEvent.set(false);
        },
      });
    } else {
      const body: UpdateAgendaEventDto = {
        clientId: payload.clientId,
        serviceId: payload.serviceId,
        statusId: payload.statusId,
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        notes: payload.notes ?? '',
        paymentCompleted: !!payload.paymentCompleted,
        professionalId: payload.professionalId || payload.agendaId,
        agendaId: payload.agendaId || payload.professionalId,
      };

      this.agendaApi.updateAgendaEvent(payload.eventId, body).subscribe({
        next: () => {
          this.toastService.show('Evento actualizado correctamente', 'success');
          this.loadVisibleRange();
          this.openForm = false;
          this.savingEvent.set(false);
        },
        error: (err: HttpErrorResponse) => {
          const errorMessage = this.extractAgendaErrorMessage(err);
          this.toastService.show(errorMessage, 'warning', 6000);
          this.savingEvent.set(false);
        },
      });
    }
  }

  /** Create multiple events sequentially (multi-service appointment) */
  private createMultiServiceEvents(events: any[]): void {
    const calls$ = events.map(ev => {
      const start = mergeDateAndTime(ev.startDate, ev.startTime);
      const end = mergeDateAndTime(ev.endDate, ev.endTime);
      if (end <= start) end.setDate(end.getDate() + 1);

      const body: CreateAgendaEventDto = {
        ...ev,
        startDate: start,
        endDate: end,
      };
      return this.agendaApi.createAgendaEvent(body);
    });

    // Execute sequentially with concat
    concat(...calls$).pipe(toArray()).subscribe({
      next: () => {
        this.openForm = false;
        this.savingEvent.set(false);
        this.loadVisibleRange();
        this.toastService.show(
          `${events.length} servicios agendados correctamente`,
          'success',
        );
      },
      error: (err: HttpErrorResponse) => {
        const errorMessage = this.extractAgendaErrorMessage(err);
        this.toastService.show(errorMessage, 'warning', 6000);
        this.savingEvent.set(false);
        // Reload to show any events that were created before the error
        this.loadVisibleRange();
      },
    });
  }

  openDelete(eventId: any) {
    this.toDeleteId = eventId;
    this.confirmOpen = true;
  }

  onQuickDelete() {
    if (!this.toDeleteId) return;

    this.agendaApi.deleteAgendaEvent(this.toDeleteId).subscribe({
      next: (res) => {
        this.fc?.getEventById(this.toDeleteId!)?.remove();
        this.open = false;
        this.selectedEventId.set(null);
        this.toastService.show(
          'Evento eliminado correctamente',
          'success',
          4000
        );
      },
      error: (e) => console.error(e),
    });
  }

  onCreateBlock() {
    // Today when it is on screen, otherwise the first visible day
    const view = this.calendar?.getApi().view;
    const date = this.rangeHasToday() || !view ? new Date() : view.activeStart;

    // When the calendar shows a single professional, the block is most likely for them
    const shown = this.filterSvc.filteredIds().filter((id) => id !== '__none__');

    this.openBlockForm = true;
    this.modalBodyInputsBlock = {
      selectedDate: date,
      selectedProfessionalId: shown.length === 1 ? shown[0] : null,
    };
  }

  onBlockModalConfirm(payload: any) {
    if (!payload) {
      this.openBlockForm = false;
      return;
    }

    if (this.savingBlock()) return;
    this.savingBlock.set(true);
    this.scheduleService.createScheduleBlock(payload).subscribe({
      next: () => {
        this.savingBlock.set(false);
        this.toastService.show('Horario bloqueado', 'success');
        this.openBlockForm = false;
        this.loadVisibleRange();
      },
      error: (err: HttpErrorResponse) => {
        this.savingBlock.set(false);
        const api = err.error as { message?: string; details?: string };
        const msg =
          api?.details ||
          api?.message ||
          err.message ||
          'Ocurrió un error al crear el bloqueo.';
        this.toastService.show(msg, 'warning', 4000);
      },
    });
  }

  onQuickEdit(eventId: string) {
    this.open = false;
    this.openForm = true;

    this.modalBodyInputs = {
      editEventId: eventId,
    };

    this.baseTitle = 'Editar evento';
    this.titleSelected.set('Editar evento');
  }

  onFinalPriceChange = (price: number): void => {
    console.log(
      'onFinalPriceChange called with price:',
      price,
      'baseTitle:',
      this.baseTitle
    );
    // Ensure baseTitle has a value (fallback to 'Crear evento' if empty)
    const base = this.baseTitle || 'Crear evento';

    // Format price with same format as in HTML: number:'1.0-2'
    // This replicates the Angular number pipe format '1.0-2' (min 1 digit, 0-2 decimals)
    const formattedPrice = new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      useGrouping: true, // Enable thousands separator
    }).format(price);

    // Update title with base title and formatted price
    if (price > 0) {
      this.titleSelected.set(`${base} · Total estimado: $${formattedPrice}`);
    } else {
      // If no price, show only base title
      this.titleSelected.set(base);
    }
    console.log('Updated titleSelected to:', this.titleSelected());
  };

  onEventSaved = () => {
    this.open = false;
    this.eventModalSize.set('sm');
    this.loadVisibleRange();
  };

  onEventPayment = (eventId?: string | null) => {
    // Payment is now handled inline within EventView, no need to open drawer
  };

  onEventSizeChanged = (size: 'sm' | 'md' | 'md-1' | 'lg' | 'xl' | '2xl') => {
    this.eventModalSize.set(size);
  };

  onPaymentCompleted = () => {
    this.loadVisibleRange();
  };

  private toEventInput = (e: AgendaEvent): EventInput => {
    // Check if this is a block event
    const isBlock = (e as any).extendedProps?.type === 'block';

    if (isBlock) {
      const blockTitle =
        (e as any).extendedProps?.title || e.notes || 'Bloqueo';

      // Preserve original extendedProps (especially scheduleBlockId)
      const originalExtendedProps = (e as any).extendedProps || {};

      return {
        id: (e as any).eventId ?? (e as any).id,
        title: blockTitle,
        start: parseDbUtc(e.startDate),
        end: parseDbUtc(e.endDate),
        allDay: false,
        borderColor: '#DC2626',
        backgroundColor: '#FEE2E2',
        textColor: '#991B1B',
        display: 'background',
        extendedProps: {
          ...e,
          ...originalExtendedProps, // Preserve scheduleBlockId and other original props
          stripeColor: '#DC2626',
          type: 'block',
        },
      };
    }

    const professionalId = e.professionalId || e.agendaId || '';
    // Use professional color from backend, fallback to agendaById map, then default color
    const professionalColor = e.professionalColor || e.color || this.agendaById().get(professionalId) || '#3B82F6';
    const statusColor = this.statusDefs[e.statusCode]?.color ?? '#9CA3AF';

    // Preserve original extendedProps if they exist (important for blocks)
    const originalExtendedProps = (e as any).extendedProps || {};

    return {
      id: (e as any).eventId ?? (e as any).id,
      title: (e as any).title ?? (e as any).clientName ?? 'Evento',
      start: parseDbUtc(e.startDate),
      end: parseDbUtc(e.endDate),
      allDay: false,
      borderColor: professionalColor,
      backgroundColor: professionalColor,
      extendedProps: {
        ...e,
        ...originalExtendedProps, // Preserve original extendedProps (type, scheduleBlockId, etc.)
        stripeColor: professionalColor,
        statusColor,
        statusLabel: this.statusLabel(e.statusName || this.statusDefs[e.statusCode]?.name),
      },
    };
  };

  private extractAgendaErrorMessage(err: HttpErrorResponse | Error): string {
    const fallback = 'Ocurrió un error inesperado. Intenta nuevamente.';

    // Handle case where err is a simple Error (shouldn't happen after fix, but just in case)
    if (err instanceof Error && !('error' in err)) {
      console.log('Error is a simple Error object:', err.message);
      return err.message || fallback;
    }

    // Handle HttpErrorResponse (normal case)
    const httpError = err as HttpErrorResponse;
    const errorResponse = httpError?.error;

    // If errorResponse is null/undefined, try err.message
    if (!errorResponse) {
      console.log('No errorResponse, checking err.message');
      if (httpError?.message && !httpError.message.includes('Http failure')) {
        return httpError.message;
      }
      return fallback;
    }

    // Handle string error (shouldn't happen but just in case)
    if (typeof errorResponse === 'string') {
      try {
        const parsed = JSON.parse(errorResponse);
        return this.extractMessageFromErrorObject(parsed, fallback);
      } catch {
        return errorResponse || fallback;
      }
    }

    // Handle object error (normal case)
    if (typeof errorResponse === 'object') {
      return this.extractMessageFromErrorObject(errorResponse, fallback);
    }

    return fallback;
  }

  private extractMessageFromErrorObject(
    errorObj: any,
    fallback: string
  ): string {
    // Priority 1: Check for errors array (this is what backend returns)
    if (
      errorObj.errors &&
      Array.isArray(errorObj.errors) &&
      errorObj.errors.length > 0
    ) {
      // Filter out generic messages
      const relevantErrors = errorObj.errors.filter(
        (e: string) =>
          e &&
          typeof e === 'string' &&
          !e.includes('error interno del servidor') &&
          !e.includes('Internal Server Error') &&
          !e.includes('Ha ocurrido un error interno del servidor')
      );

      if (relevantErrors.length > 0) {
        const message = relevantErrors.join('<br>');
        return message;
      }

      return errorObj.errors[0] || fallback;
    }

    // Priority 2: Check for message field
    if (errorObj.message && typeof errorObj.message === 'string') {
      return errorObj.message;
    }

    // Priority 3: Check for details field
    if (errorObj.details && typeof errorObj.details === 'string') {
      return errorObj.details;
    }

    return fallback;
  }

  private normalizeErrorPayload(raw: unknown): any {
    if (!raw) return {};

    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        return { message: raw };
      }
    }

    if (raw instanceof ArrayBuffer) {
      try {
        const text = new TextDecoder().decode(raw);
        return JSON.parse(text);
      } catch {
        return {};
      }
    }

    return raw;
  }

  private applyPendingEvents() {
    if (!this.fc || !this.pendingEvents) return;
    this.fc.removeAllEvents();
    this.fc.addEventSource(this.pendingEvents);
    this.pendingEvents = null;
  }

  private applyFilterToCalendar() {
    const ids = this.filterSvc.filteredIds();

    // Business-wide blocks have no professional and stay visible under any filter
    const filtered = ids.length
      ? this.lastFetchedEvents.filter((e) => {
          const pid = e.professionalId || e.agendaId || '';
          return ids.includes(pid) || ((e as any).extendedProps?.type === 'block' && !pid);
        })
      : this.lastFetchedEvents;

    const evs = filtered.map(this.toEventInput);

    if (this.fc) {
      this.fc.removeAllEvents();
      this.fc.addEventSource(evs);
    } else {
      this.pendingEvents = evs;
    }
  }
}
